// Catalog stream taxonomy — shared by UniversityBrandAdminPage's
// CatalogStreamsCard, CreateCourseModal, and EducationAdminPage pickers.
//
// Owner toggle vocabulary:  standard | elite | university | community | church
// Row classification:
//   community  : source === 'community' OR is_community_published = true
//   elite      : subject_group === 'Elite Academy'
//                OR curriculum_track in foundation/intermediate/advanced
//   church     : in_church_catalog = true
//                OR curriculum_track === 'faith'
//                OR subject_group in 'Christian Studies'/'Bible Journey'
//   university : facility_id === UNIVERSITY_FACILITY_ID (0002)
//                AND NOT church (church markers override university)
//   standard   : everything else — master K-12 foundational classes
//
// Facility IDs (immutable master facilities):
//   MASTER_FACILITY_ID     = 00000000-0000-0000-0000-000000000001  (K-12 master)
//   UNIVERSITY_FACILITY_ID = 00000000-0000-0000-0000-000000000002  (BSc degrees)
//
// Persisted per-org at facilities.branding.catalog_streams (JSONB — no
// migration needed; org admins already UPDATE facilities.branding from the
// brand console). Absent or malformed value => ALL streams on, so existing
// orgs keep today's behaviour until an admin saves toggles.
// Pick-time curation only — NOT a security boundary; RLS and copy-time
// checks remain the boundary.
import { supabase } from '@/integrations/supabase/client'

export const MASTER_FACILITY_ID     = '00000000-0000-0000-0000-000000000001'
export const UNIVERSITY_FACILITY_ID = '00000000-0000-0000-0000-000000000002'

export type CatalogStream = 'standard' | 'elite' | 'university' | 'community' | 'church'

export const ALL_STREAMS: { key: CatalogStream; label: string; hint: string }[] = [
  { key: 'standard', label: 'Standard', hint: 'Core foundational master classes (ages 5–15)' },
  { key: 'elite', label: 'Elite', hint: 'Elite Academy / foundation-intermediate-advanced tracks' },
  { key: 'university', label: 'University', hint: 'Higher-education & collegiate courses (Calculus, Data Science, Accounting, 16+)' },
  { key: 'community', label: 'Community', hint: 'Community-published course library' },
  { key: 'church', label: 'Faith / Church', hint: 'Christian Studies & Biblical curriculum' },
]

export const DEFAULT_STREAMS: CatalogStream[] = ALL_STREAMS.map(s => s.key)

const ELITE_TRACKS = ['foundation', 'intermediate', 'advanced']
const FAITH_GROUPS = ['christian studies', 'bible journey']

export function normalizeCatalogStreams(raw: unknown): CatalogStream[] {
  if (!Array.isArray(raw)) return DEFAULT_STREAMS            // absent => all on
  const keys = ALL_STREAMS.map(s => s.key)
  return keys.filter(k => (raw as unknown[]).includes(k))    // [] only if every stream was saved off (the card blocks that)
}

export function classifyStream(c: any): CatalogStream {
  if (!c) return 'standard'

  // Community library rows — published-community flag or community source.
  if (c.is_community_published === true || String(c.source ?? '').toLowerCase() === 'community' || c.curriculum_track === 'community') {
    return 'community'
  }

  const group  = String(c.subject_group    ?? '').toLowerCase()
  const track  = String(c.curriculum_track ?? '').toLowerCase()
  const fid    = String(c.facility_id      ?? '')

  // 1. Church / Faith stream (Bible Journey 9 courses)
  if (track === 'faith' || group === 'bible journey' || group.includes('bible journey')) {
    return 'church'
  }

  // 2. Elite Academy (30 courses across foundation/intermediate/advanced tracks)
  if (group === 'elite academy' || ELITE_TRACKS.includes(track)) {
    return 'elite'
  }

  // 3. University: higher-education courses living in the University facility (0002)
  if (fid === UNIVERSITY_FACILITY_ID) {
    return 'university'
  }

  // 4. Standard: foundational master classes (including Christian Studies standard subjects)
  return 'standard'
}

export function classVisibleInStreams(c: any, streams: CatalogStream[]): boolean {
  return streams.includes(classifyStream(c))
}

/** Read-modify-write merge — never clobbers sibling branding keys (org_type, theme, ...). */
export async function saveOrgCatalogStreams(facilityId: string, streams: CatalogStream[]): Promise<void> {
  const { data: fac, error: e1 } = await supabase
    .from('facilities')
    .select('branding')
    .eq('id', facilityId)
    .single()
  if (e1 || !fac) throw new Error(e1?.message || 'Facility not found')
  const branding = { ...((fac.branding as Record<string, unknown>) ?? {}), catalog_streams: streams }
  const { error: e2 } = await supabase.from('facilities').update({ branding }).eq('id', facilityId)
  if (e2) throw new Error(e2.message)
}

/**
 * Loads the org's stream config. Pass the admin's facility_id when you have
 * it (both pickers use useAuth().userProfile?.facility_id). Falls back to ALL
 * streams on any failure — a wrong id can never blank the pickers.
 */
export async function loadOrgCatalogStreams(facilityId?: string | null): Promise<CatalogStream[]> {
  if (!facilityId) return DEFAULT_STREAMS
  try {
    const { data: fac } = await supabase
      .from('facilities')
      .select('branding')
      .eq('id', facilityId)
      .maybeSingle()
    return normalizeCatalogStreams((fac?.branding as Record<string, unknown> | null)?.catalog_streams)
  } catch {
    return DEFAULT_STREAMS
  }
}